import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://crossfit-saintchamond.fr';
const GYM_NAME = 'CrossFit Saint-Chamond';
const GYM_PHONE = '+33 6 84 28 40 87';

/**
 * Course schema.org for each programme page (/programmes/:slug).
 * Helps Google surface the programme in "cours près de chez vous" and enables
 * rich snippets. Also fed to GPTBot/ClaudeBot/PerplexityBot for AEO.
 */
export function CourseJsonLd({ slug, title, description, duration, capacity, level, heroImage }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `${title} — ${GYM_NAME}`,
    description,
    url: `${SITE_URL}/programmes/${slug}`,
    image: heroImage,
    inLanguage: 'fr-FR',
    educationalLevel: level,
    provider: {
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#LocalBusiness`,
      name: GYM_NAME,
      url: SITE_URL,
      telephone: GYM_PHONE,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '7 Rue Jean-Jacques Rousseau',
        postalCode: '42400',
        addressLocality: 'Saint-Chamond',
        addressRegion: 'Loire',
        addressCountry: 'FR',
      },
    },
    offers: {
      '@type': 'Offer',
      category: 'Cours collectif',
      priceCurrency: 'EUR',
      price: '59.00',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/tarifs`,
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'onsite',
      name: title,
      courseWorkload: duration,
      location: {
        '@type': 'Place',
        name: GYM_NAME,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '7 Rue Jean-Jacques Rousseau',
          postalCode: '42400',
          addressLocality: 'Saint-Chamond',
          addressCountry: 'FR',
        },
      },
      instructor: {
        '@type': 'Person',
        name: 'Nicolas Perroy',
        jobTitle: 'Coach CrossFit certifié',
      },
      maximumAttendeeCapacity: parseInt(capacity) || undefined,
      offers: {
        '@type': 'Offer',
        category: 'Cours collectif',
        priceCurrency: 'EUR',
        price: '59.00',
        availability: 'https://schema.org/InStock',
      },
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

/**
 * Compute the next N "first Sunday of the month" dates.
 * Each event is at 09:30 local time, duration 90 min.
 */
function nextTeamXlDates(count = 6) {
  const out = [];
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth(); // 0-11
  while (out.length < count) {
    // Find first Sunday of (year, month)
    const d = new Date(year, month, 1, 9, 30, 0);
    const offset = (7 - d.getDay()) % 7; // 0 if already Sunday
    d.setDate(1 + offset);
    // Only keep if in the future
    if (d.getTime() > now.getTime() - 24 * 3600 * 1000) {
      out.push(d);
    }
    // Next month
    month += 1;
    if (month > 11) { month = 0; year += 1; }
  }
  return out;
}

/**
 * Event schema for the monthly WOD Team XL.
 * Emits one Event per upcoming month, so IA assistants (ChatGPT/Perplexity/etc.)
 * can answer "quand a lieu le prochain WOD Team XL ?" with a concrete date.
 */
export function TeamXlEventsJsonLd() {
  const events = nextTeamXlDates(6).map((startDate) => {
    const endDate = new Date(startDate.getTime() + 90 * 60 * 1000);
    const iso = (d) => d.toISOString().replace(/\.\d{3}Z$/, '+02:00');
    return {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: `WOD Team XL — CrossFit Saint-Chamond (${startDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })})`,
      description: "Rendez-vous mensuel grand format en équipe jusqu'à 18 athlètes. Challenge collectif CrossFit/HYROX inédit. Ouvert à tous les niveaux, scaling possible. Premier dimanche du mois.",
      startDate: iso(startDate),
      endDate: iso(endDate),
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      inLanguage: 'fr-FR',
      location: {
        '@type': 'Place',
        name: GYM_NAME,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '7 Rue Jean-Jacques Rousseau',
          postalCode: '42400',
          addressLocality: 'Saint-Chamond',
          addressRegion: 'Loire',
          addressCountry: 'FR',
        },
      },
      image: 'https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/k4arxv2t_WhatsApp%20Image%202026-04-02%20at%2014.37.45.jpeg',
      organizer: {
        '@type': 'Organization',
        name: GYM_NAME,
        url: SITE_URL,
      },
      performer: {
        '@type': 'Person',
        name: 'Nicolas Perroy',
        jobTitle: 'Coach CrossFit certifié',
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/reservation`,
        validFrom: iso(new Date()),
        description: 'Inclus dans les abonnements Illimité. Réservation obligatoire via Deciplus.',
      },
      maximumAttendeeCapacity: 18,
    };
  });
  return (
    <Helmet>
      {events.map((e, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(e)}</script>
      ))}
    </Helmet>
  );
}
